import React, { useState, useEffect } from "react";
import {
  Calculator,
  Calendar,
  Clock,
  CheckCircle,
  Loader,
} from "lucide-react";
import { validateDate, isAuthenticated } from "../../utils/auth";
import { useNavigate, useLocation } from "react-router-dom";import { getStemColor,getBranchColor,STEMS_SET,BRANCHES_SET } from "../_helpers"; // <-- 新增导入
import apiService from '../../services/api';

const Bazi = ({ currentView, setCurrentView, isLoggedIn, onLogout }) => {
  const navigate = useNavigate();

  // ==================== 状态管理 ====================
  // 表单数据 - 出生日期时间
  const [formData, setFormData] = useState({
    mingzhu_name: "",
    year: "",
    month: "",
    day: "",
    hour: "",
    gender: "", // 去掉默认值，初始未选择
    shudi: "",
    zhanti_type: "",
    zhanti: "",
  });

  // 计算结果
  const [result, setResult] = useState(null);

  // UI状态
  const [loading, setLoading] = useState(false); // 计算中
  const [errors, setErrors] = useState({}); // 字段错误信息
  const [success, setSuccess] = useState(""); // 成功信息

  // 占类选项
  const [zhantiOptions, setZhantiOptions] = useState([]);

  // 获取占类选项
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const types = await apiService.getZhantiTypes();
        if (types && types.length > 0) {
          setZhantiOptions(types);
        }
      } catch (error) {
        console.error("Failed to load zhanti types", error);
      }
    };
    fetchTypes();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // 更新表单数据
    setFormData((prev) => ({
      ...prev,
      // gender, shudi, zhanti_type, zhanti 保持字符串，其他字段转为整数
      [name]:
        value === ""
          ? ""
          : (name === "gender" || name === "shudi" || name === "zhanti_type" || name === "zhanti" || name === "mingzhu_name")
          ? value
          : parseInt(value),
    }));

            // 清除对应字段错误
            setErrors((prev) => ({ ...prev, [name]: "", submit: "" }));
    if (success) setSuccess("");
  };

  const handleDateChange = (field, value) => {
    // Allow empty string for editing
    if (value === '') {
      setFormData(prev => ({ ...prev, [field]: '' }));
      setErrors((prev) => ({ ...prev, [field]: "", submit: "" }));
      return;
    }

    const num = parseInt(value);

    // Ban non-numeric or negative
    if (isNaN(num) || num < 0) return;

    // Validation
    if (field === 'year' && num > 2100) return;
    if (field === 'month' && (num > 12 || num === 0)) return;
    if (field === 'day' && (num > 31 || num === 0)) return;
    if (field === 'hour' && num > 23) return;

    // Update state
    setFormData(prev => ({ ...prev, [field]: num }));
    setErrors((prev) => ({ ...prev, [field]: "", submit: "" }));
    if (success) setSuccess("");
  };

  const [bzChart, setBZChart] = useState(null);
  
  // ==================== 八字计算 ====================
  const handleCalculate = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccess("");
 

    // 验证输入
    const { year, month, day, hour, gender } = formData;
    const err = {};
    if (!year) err.year = "请填写年份";
    if (!month) err.month = "请填写月份";
    if (!day) err.day = "请填写日期";
    if (hour === "") err.hour = "请填写小时";
    if (!gender || gender === "") err.gender = "请选择性别";
    if (year && (year < 1900 || year > 2100)) {
      err.year = "出生年份应在1900-2100之间";
    }

    if (Object.keys(err).length > 0) {
      setErrors(err);
      return;
    }

    // 验证日期有效性
    const dateValidation = validateDate(year, month, day, hour);
    if (!dateValidation.valid) {
      setErrors({ day: dateValidation.error });
      return;
    }

    setLoading(true);

    try {
      // 过滤姓名：仅允许中英文、数字，禁止特殊符号
      let filteredName = formData.mingzhu_name.replace(/[^\u4e00-\u9fa5a-zA-Z0-9]/g, "");
      if (filteredName.length > 10) {
        filteredName = filteredName.substring(0, 10);
      }

      // 构造请求数据
      const requestData = {
        mingzhu_name: filteredName,
        gender: String(gender),
        year: String(year),
        month: String(month),
        day: String(day),
        hour: String(hour),
        shudi: formData.shudi || "",
        zhanti_type: formData.zhanti_type || "",
        zhanti: formData.zhanti || "",
      };

      const response = await apiService.calculateBazi(requestData);

      if (response.data.success) {
        const chartData = response.data.data;
        setBZChart(chartData);  
        // 导航到专页，传递完整数据
        navigate('/bazi/baziDetail', { state: { result: chartData, form: formData } });
      } else {
        setErrors({ submit: response.data.error || "计算失败" });
      }

    } catch (err) {
      console.error("计算错误:", err);
      setErrors({ submit: err.message || err.response?.data?.error || "计算过程中出现错误，请重试" });
      setResult(null);
    } finally {
      setLoading(false);
    }
  };


  // ==================== 渲染组件 ====================
  // Navbar 放在最外层，保证通栏（full-width）
  return (
    <>
      <div className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden md:max-w-2xl p-6 space-y-6">
          {/* 页面标题 */}
          <div className="text-center border-b pb-4">
            <h2 className="text-2xl font-bold text-gray-900">八字排盘计算</h2>
            <p className="text-gray-600 text-sm">请输入准确的出生日期和时间（公历/阳历）</p>
          </div>
          <form onSubmit={handleCalculate} className="space-y-4">
            {/* 姓名+性别+属地 */}
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-1">
                <input
                  type="text"
                  name="mingzhu_name"
                  value={formData.mingzhu_name || ""}
                  onChange={handleInputChange}
                  className="block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2.5"
                  placeholder="姓名"
                  maxLength={10}
                />
              </div>
              <div className="col-span-1">
                <select
                  name="gender"
                  value={formData.gender || ""}
                  onChange={handleInputChange}
                  className={`block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border px-2.5 bg-white ${formData.gender ? "text-gray-700" : "text-gray-400"}`}
                >
                  <option value="">性别（必）</option>
                  <option value="male">男</option>
                  <option value="female">女</option>
                </select>
                {errors.gender && <div className="text-red-600 text-xs mt-1">{errors.gender}</div>}
              </div>
              <div className="col-span-1">
                <input
                  type="text"
                  name="shudi"
                  value={formData.shudi || ""}
                  onChange={handleInputChange}
                  className="block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2.5"
                  placeholder="属地（选填）"
                />
              </div>
            </div>

            {/* 出生时间 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">出生时间</label>
              <div className="grid grid-cols-4 gap-2">
                <div className="relative">
                  <input type="number" value={formData.year} onChange={e => handleDateChange('year', e.target.value)} className="block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2.5 text-center" placeholder="年" />
                  <span className="absolute right-1 top-2.5 text-gray-400 text-xs pointer-events-none">年</span>
                  {errors.year && <div className="text-red-600 text-xs mt-1">{errors.year}</div>}
                </div>
                <div className="relative">
                  <input type="number" value={formData.month} onChange={e => handleDateChange('month', e.target.value)} className="block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2.5 text-center" placeholder="月" />
                  <span className="absolute right-1 top-2.5 text-gray-400 text-xs pointer-events-none">月</span>
                  {errors.month && <div className="text-red-600 text-xs mt-1">{errors.month}</div>}
                </div>
                <div className="relative">
                  <input type="number" value={formData.day} onChange={e => handleDateChange('day', e.target.value)} className="block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2.5 text-center" placeholder="日" />
                  <span className="absolute right-1 top-2.5 text-gray-400 text-xs pointer-events-none">日</span>
                  {errors.day && <div className="text-red-600 text-xs mt-1">{errors.day}</div>}
                </div>
                <div className="relative">
                  <input type="number" value={formData.hour} onChange={e => handleDateChange('hour', e.target.value)} className="block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2.5 text-center" placeholder="时" />
                  <span className="absolute right-1 top-2.5 text-gray-400 text-xs pointer-events-none">时</span>
                  {errors.hour && <div className="text-red-600 text-xs mt-1">{errors.hour}</div>}
                </div>
              </div>
            </div>

            {/* 占类和求占问题 */}
            <div className="grid grid-cols-4 gap-2">
              <div className="col-span-1">
                <select
                  name="zhanti_type"
                  value={formData.zhanti_type || ""}
                  onChange={handleInputChange}
                  className={`block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2.5 bg-white ${formData.zhanti_type ? "text-gray-700" : "text-gray-400"}`}
                >
                  <option value="">占类</option>
                  {zhantiOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-span-3">
                <input
                  type="text"
                  name="zhanti"
                  value={formData.zhanti || ""}
                  onChange={handleInputChange}
                  className="block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2.5"
                  placeholder="求占问题（选填）"
                  maxLength={100}
                />
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="pt-4 flex justify-center">
              <button
                type="submit"
                disabled={loading}
                className="px-8 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200 min-w-[120px] flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader className="h-5 w-5 mr-2 animate-spin" />
                    计算中...
                  </>
                ) : (
                  <>
                    <Calculator className="h-5 w-5 mr-2" />
                    开始计算
                  </>
                )}
              </button>
            </div>
            {errors.submit && (
              <div className="text-red-600 text-xs text-center bg-red-50 p-1.5 rounded border border-red-100">
                {errors.submit}
              </div>
            )}
          </form>
        </div>

        {/* 成功提示 */}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-start animate-fade-in">
            <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{success}</span>
          </div>
        )}



      </div>

    </>
  );
};

export default Bazi;
